import { ApiResponse } from '@/utils';
import { enhancedRequest } from '@/utils/enhancedRequest';
import {
  UserCreditsParams,
  UserCreditsResponse,
  UserBenefitsParams,
  UserBenefitsResponse,
  UserPackageParams,
  UserPackageResponse,
  UpdatePhoneParams,
  UpdateProfileParams,
  CreationTaskListResponse,
  GetCreationTaskListParams,
  CreationTaskStatus,
  CreationTaskItem,
  UploadDeliverablesFileParams,
  GetTaskHallParams,
  TaskHallResponse,
  CompanyAuthApplyParams,
  CreatorAuthApplyParams,
  CreatorAuthApplyResponse,
} from '../types';

/**
 * AMAI用户服务
 * 用于处理用户相关功能，如积分流水查询
 */
class UserService {
  /**
   * 获取用户积分流水
   * @param params 查询参数 { need_record?, record_type?, page_number?, page_size? }
   * @returns 用户积分信息和流水记录
   */
  async getUserCredits(params: UserCreditsParams = {}): Promise<ApiResponse<UserCreditsResponse>> {
    // 设置默认参数
    const requestParams = {
      need_record: false,
      record_type: 0,
      page_number: 1,
      page_size: 20,
      ...params,
    };

    // 验证参数范围
    if (requestParams.page_size < 20 || requestParams.page_size > 100) {
      requestParams.page_size = 20;
    }

    return enhancedRequest.post<UserCreditsResponse>('/user/credits', requestParams, {
      useCache: !requestParams.need_record, // 如果不需要流水记录，可以使用缓存
      cacheTTL: 2 * 60 * 1000, // 积分信息缓存2分钟
      enableDeduplication: true,
      requestKey: `user-credits:${JSON.stringify(requestParams)}`,
      retryCount: 2,
      timeout: 10000, // 积分查询10秒超时
    });
  }

  /**
   * 获取用户积分余额（不包含流水记录）
   * @returns 用户积分余额信息
   */
  async getUserCreditsBalance(): Promise<ApiResponse<UserCreditsResponse>> {
    return this.getUserCredits({
      need_record: false,
    });
  }

  /**
   * 获取用户积分流水记录
   * @param recordType 流水类型（0=全部；1=购买；2=消耗；3=获得）
   * @param pageNumber 页码，默认为1
   * @param pageSize 页条，默认为20
   * @returns 用户积分流水记录
   */
  async getUserCreditsHistory(
    recordType: number = 0,
    pageNumber: number = 1,
    pageSize: number = 20
  ): Promise<ApiResponse<UserCreditsResponse>> {
    return this.getUserCredits({
      need_record: true,
      record_type: recordType,
      page_number: pageNumber,
      page_size: pageSize,
    });
  }

  /**
   * 被动发放权益
   * 前端发起接口调用，获取用户可领取的权益
   * @param params 空对象参数
   * @returns 发放的权益列表
   */
  async getUserBenefits(
    params: UserBenefitsParams = {}
  ): Promise<ApiResponse<UserBenefitsResponse>> {
    return enhancedRequest.post<UserBenefitsResponse>('/user/benefits', params, {
      enableDeduplication: true,
      requestKey: 'user-benefits',
      retryCount: 2,
      timeout: 10000, // 权益查询10秒超时
    });
  }

  /**
   * 
   * @returns 立即领取新人礼包
   */
  async getNewcomerPack(captchaVerifyParam: string): Promise<ApiResponse<any>> {
    return enhancedRequest.post<any>('/invite/claim_new_user_gift', { captcha_verify_param: captchaVerifyParam }, {
      retryCount: 0,
    });
  } 


  /**
   * 获取用户当前的套餐
   * @param params 空对象参数
   * @returns 用户已购买的套餐列表和免费套餐权益
   */
  async getUserPackage(params: UserPackageParams = {}): Promise<ApiResponse<UserPackageResponse>> {
    return enhancedRequest.post<UserPackageResponse>('/user/package', params, {
      useCache: true,
      cacheTTL: 5 * 60 * 1000, // 用户套餐信息缓存5分钟
      enableDeduplication: true,
      requestKey: 'user-package',
      retryCount: 2,
      timeout: 10000, // 套餐查询10秒超时
    });
  }

  /**
   * 更新用户手机号
   * @param params 请求参数 {mobile: string, country_code?: string, sms_code: string}
   * @returns 基础响应
   */
  async updatePhone(params: UpdatePhoneParams): Promise<ApiResponse<any>> {
    // 设置默认国家区号
    const requestParams = {
      country_code: '86',
      ...params,
    };

    // 验证参数
    if (!requestParams.mobile) {
      throw new Error('手机号不能为空');
    }

    if (!requestParams.sms_code) {
      throw new Error('短信验证码不能为空');
    }

    return enhancedRequest.post<any>('/user/update-mobile', requestParams, {
      enableDeduplication: true,
      requestKey: `update-phone:${requestParams.mobile}:${requestParams.sms_code}`,
      retryCount: 1,
      timeout: 15000, // 更新手机号15秒超时
    });
  }

  /**
   * 获取创作任务列表
   * @param params 查询参数 { status?, page_number?, page_size? }
   * @returns 创作任务列表
   */
  async getCreationTaskList(
    params: GetCreationTaskListParams = {}
  ): Promise<ApiResponse<CreationTaskListResponse>> {
    return enhancedRequest.get<CreationTaskListResponse>('/task/project-task', params, {
      enableDeduplication: true,
      requestKey: `user-tasks:${JSON.stringify(params)}`,
      retryCount: 1,
      timeout: 10000,
      forceRefresh: true,
    });
  }

  /**
   * 获取任务大厅列表
   * @param params 查询参数 { status?: string, page_number?: number, page_size?: string }
   * @returns 任务大厅列表数据
   */
  async getTaskHall(
    params: GetTaskHallParams = {}
  ): Promise<ApiResponse<TaskHallResponse>> {
    return enhancedRequest.get<TaskHallResponse>('/task/project-task/hall', params, {
      enableDeduplication: true,
      requestKey: `task-hall:${JSON.stringify(params)}`,
      retryCount: 1,
      timeout: 10000,
      forceRefresh: true,
    });
  }
    /**
   * 提交报名
   * @param params 查询参数 { status?: string, page_number?: number, page_size?: string }
   * @returns 任务大厅列表数据
   */
  async signUp(
    params:  {}
  ): Promise<ApiResponse<any>> {
    return enhancedRequest.post<any>('/task/project/signup', params, {
      enableDeduplication: true,
      requestKey: `task-hall:${JSON.stringify(params)}`,
      retryCount: 1,
      timeout: 10000,
      forceRefresh: true,
    });
  }
  async getProjectDetail(
    params:  {}
  ): Promise<ApiResponse<any>> {
    return enhancedRequest.get<any>('/task/project/detail', params, {
      enableDeduplication: true,
      requestKey: `task-hall:${JSON.stringify(params)}`,
      retryCount: 1,
      timeout: 10000,
      forceRefresh: true,
    });
  }

  /**
   * 更新用户资料（可更新微信号、手机号、昵称、个人简介）
   * @param params 请求参数 {wechat?: string, mobile?: string, sms_code?: string, user_name?: string, personal_profile?: string}
   * @returns 基础响应
   */
  async updateProfile(params: UpdateProfileParams): Promise<ApiResponse<any>> {
    // 构建请求参数，只包含有值的字段
    const requestParams: UpdateProfileParams = {};

    if (params.wechat?.trim()) {
      requestParams.wechat = params.wechat.trim();
    }
    if (params.mobile?.trim()) {
      requestParams.mobile = params.mobile.trim();
    }
    if (params.sms_code) {
      requestParams.sms_code = params.sms_code;
    }
    if (params.user_name?.trim()) {
      requestParams.user_name = params.user_name.trim();
    }
    if (params.personal_profile?.trim()) {
      requestParams.personal_profile = params.personal_profile.trim();
    }

    return enhancedRequest.post<any>('/user/update-profile', requestParams, {
      enableDeduplication: true,
      requestKey: `update-profile:${JSON.stringify(requestParams)}`,
      retryCount: 1,
      timeout: 15000, // 更新资料15秒超时
    });
  }

  /**
   * 上传交付物
   * @param params 请求参数 {file: File, task_id: string}
   * @returns 基础响应
   */
  async uploadZip(params: UploadDeliverablesFileParams): Promise<ApiResponse<any>> {
    // 验证参数
    if (!params.file) {
      throw new Error('文件不能为空');
    }

    if (!params.task_id) {
      throw new Error('任务ID不能为空');
    }

    // 构建 FormData
    const formData = new FormData();
    formData.append('file', params.file);
    formData.append('task_id', params.task_id);

    // 使用 enhancedRequest.post 上传文件
    // axios 会自动检测 FormData 并设置正确的 Content-Type
    return enhancedRequest.post<any>('/task/upload/deliverables', formData, {
      enableDeduplication: false, // 文件上传不启用去重
      retryCount: 0, // 文件上传允许重试1次
      timeout: 60 * 60 * 1000, // 文件上传1小时超时
    });
  }

  /**
   * 企业入驻申请
   * @param params 请求参数 {company_name, business_license, company_email, contact_name, contact_mobile, sms_code}
   * @returns 基础响应
   */
  async companyAuthApply(params: CompanyAuthApplyParams): Promise<ApiResponse<null>> {
    // 验证参数
    if (!params.company_name || params.company_name.trim() === '') {
      throw new Error('企业全称不能为空');
    }

    if (!params.business_license || params.business_license.trim() === '') {
      throw new Error('企业营业执照不能为空');
    }

    if (!params.company_email || params.company_email.trim() === '') {
      throw new Error('企业邮箱不能为空');
    }

    if (!params.contact_name || params.contact_name.trim() === '') {
      throw new Error('联系人姓名不能为空');
    }

    if (!params.contact_mobile || params.contact_mobile.trim() === '') {
      throw new Error('联系电话不能为空');
    }

    if (!params.sms_code || params.sms_code.trim() === '') {
      throw new Error('验证码不能为空');
    }

    return enhancedRequest.post<null>('/company/auth/apply', params, {
      enableDeduplication: true,
      requestKey: `company-auth-apply:${params.company_name}:${params.contact_mobile}`,
      retryCount: 0, // 不重试，避免重复提交
    });
  }

  /**
   * 创作者认证申请
   * @param params 请求参数 {wechat, user_name, personal_profile, apply_asset, mobile, sms_code}
   * @returns 申请响应数据
   */
  async creatorAuthApply(params: CreatorAuthApplyParams): Promise<ApiResponse<CreatorAuthApplyResponse>> {
    // 验证参数
    if (!params.wechat || params.wechat.trim() === '') {
      throw new Error('微信号不能为空');
    }

    if (!params.user_name || params.user_name.trim() === '') {
      throw new Error('昵称不能为空');
    }

    if (!params.personal_profile || params.personal_profile.trim() === '') {
      throw new Error('个人简介不能为空');
    }

    if (!params.apply_asset || params.apply_asset.length === 0) {
      throw new Error('请上传以往作品');
    }

    if (!params.mobile || params.mobile.trim() === '') {
      throw new Error('手机号不能为空');
    }

    if (!params.sms_code || params.sms_code.trim() === '') {
      throw new Error('验证码不能为空');
    }

    return enhancedRequest.post<CreatorAuthApplyResponse>('/user/auth/apply', params, {
      enableDeduplication: true,
      requestKey: `creator-auth-apply:${params.mobile}:${params.sms_code}`,
      retryCount: 0, // 不重试，避免重复提交
    });
  }
}

export default new UserService();
